import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse
} from '@angular/common/http';
import { catchError, tap, map } from 'rxjs/operators';
import { Observable, BehaviorSubject, of } from 'rxjs';

import { AccountDto } from './../account/account.dto';
import { AccountBalanceDto } from './../account/balance/balance.dto';
import { AccountListResponseDto } from './../account/account-list-response.dto';
import { AgentDataSpecDto } from './../account/agent-data-spec.dto';

import { BindingDto } from './../entity/binding.dto';
import { PageQueriesModel } from './../shared/page-queries.model';
import { PageDto } from './../shared/page.dto';
import { IdentityDto } from './../shared/identity.dto';

@Injectable()
export class AccountRestService {
  accountId: string;

  dataChange: BehaviorSubject<AccountDto[]> = new BehaviorSubject<AccountDto[]>(
    []
  );
  get data(): AccountDto[] {
    return this.dataChange.value;
  }

  searchChange: BehaviorSubject<string> = new BehaviorSubject<string>('');
  get searchValue(): string {
    return this.searchChange.value;
  }

  bindingChange = new BehaviorSubject<BindingDto[] | undefined>([]);
  get bindingData(): BindingDto[] | undefined {
    return this.bindingChange.value;
  }

  private accountManagementUrl = 'api/accountManagement';
  private fundingManagementUrl = 'api/fundingManagement';
  private accountUrl = 'api/accountManagement/accounts'; // URL to web api
  private clientUrl = 'api/accountManagement/clients';

  constructor(private http: HttpClient) {}

  list(queries: PageQueriesModel, clientId: string | null = null) {
    const url = this.getAccountsUrl(clientId);
    const options = {
      params: queries.getParams()
    };

    return this.http.get<PageDto<AccountDto>>(url, options).pipe(
      map(d => {
        const page = new PageDto(d);
        page.content = d.content.map(i => new AccountDto(i));
        return page;
      })
    );
  }

  getAccounts(queries: PageQueriesModel) {
    const options = {
      params: queries.getParams(),
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    const url = `${this.accountManagementUrl}/accounts/`;

    return this.http.get<PageDto<AccountDto>>(url, options).pipe(
      map(d => {
        const page = new PageDto(d);
        page.content = d.content.map(i => new AccountDto(i));
        return page;
      })
    );
  }

  getAccountsForClientId(
    queries: PageQueriesModel,
    clientId: string
  ) {
    const options = {
      params: queries.getParams(),
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    const url = `${this.accountManagementUrl}/clients/${clientId}/accounts`;

    return this.http.get<PageDto<AccountDto>>(url, options).pipe(
      map(d => {
        const page = new PageDto(d);
        page.content = d.content.map(i => new AccountDto(i));
        return page;
      })
    );
  }

  getAgentDataSpec(type: string) {
    const url = `${this.accountUrl}/agentdataspec/${type}`;

    return this.http
      .get<AgentDataSpecDto[]>(url)
      .pipe(
        map((d: AgentDataSpecDto[]) =>
          (d || []).map(spec => new AgentDataSpecDto(spec))
        )
      );
  }

  getAccount(id: string): Observable<AccountDto> {
    const url = `${this.accountUrl}/${id}`;
    return this.http.get<AccountDto>(url).pipe(
      tap(_ => this.log(`fetched account id=${id}`)),
      catchError(this.handleError<AccountDto>(`getAccount id=${id}`))
    );
  }

  getBalancesByAccountId(id: string): Observable<AccountBalanceDto[]> {
    const url = `${this.fundingManagementUrl}/accounts/${id}/balances`;
    return this.http
      .get<AccountBalanceDto[]>(url)
      .pipe(
        catchError(
          this.handleError<AccountBalanceDto[]>('getBalancesByAccountId')
        )
      );
  }

  update(account: AccountDto): Observable<AccountDto> {
    const parent = account.entity.parent as IdentityDto;
    const url = `${this.accountManagementUrl}/clients/${parent.id}/accounts/${
      account.identity.id
    }`;
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientId: parent.id
      })
    };
    return this.http.put(url, account, options).pipe(
      tap((acc: AccountDto) => {
        if (acc.errors) {
          this.log(`updated account failed with errors`);
        } else {
          this.log(`updated account id=${acc.identity.id}`);
        }
      }),
      catchError(this.handleError<AccountDto>('update'))
    );
  }

  put(clientId: string, accountId: string, account: AccountDto) {
    const url = `${
      this.accountManagementUrl
    }/clients/${clientId}/accounts/${accountId}`;

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientId: clientId
      })
    };

    return this.http.put(url, account, options).pipe(
      tap((acc: AccountDto) => {
        if (acc.errors) {
          this.log(`patched account failed with errors`);
        } else {
          this.log(`patched account id=${acc.identity.id}`);
          this.bindingChange.next(acc.entity.bindings);
        }
      })
    );
  }

  search(searchString: string): void {
    this.searchChange.next(searchString);
  }

  post(clientId: string, account: AccountDto) {
    const url = `${this.clientUrl}/${clientId}/accounts`;
    return this.http
      .post<AccountDto>(url, account)
      .pipe(map(i => new AccountDto(i)));
  }

  save(account: AccountDto): Observable<AccountDto> {
    const url = `${this.accountUrl}`;
    const parent = account.entity.parent as IdentityDto;
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientId: parent.id
      })
    };
    return this.http.put(url, account, options).pipe(
      tap((updatedAccount: AccountDto) => {
        if (updatedAccount.errors) {
          this.log(`updating account failed w/ errors`);
        } else {
          this.log(`updated account w/ id=${updatedAccount.identity.id}`);
        }
      }),
      catchError(this.handleError<AccountDto>('save'))
    );
  }

  getBindingsByClientId(id: string): Observable<AccountDto> {
    const url = `${this.accountUrl}/${id}`;
    return this.http.get<AccountDto>(url).pipe(
      tap(_ => this.log(`fetched productline id=${id}`)),
      catchError(this.handleError<AccountDto>(`getBindingsByClientId id=${id}`))
    );
  }

  getAccountsByLegalEntityId(id: string): Observable<AccountListResponseDto> {
    return this.http
      .get<AccountListResponseDto>(
        `${this.accountManagementUrl}/accounts?search=legalEntityId=='${id}'`
      )
      .pipe(
        catchError(
          this.handleError<AccountListResponseDto>('getPaginatedProductlines')
        )
      );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  private log(message: string) {
    console.log('Account Service: ' + message);
  }

  private getAccountsUrl(id: string | null): string {
    return id
      ? `${this.accountManagementUrl}/clients/${id}/accounts`
      : this.accountUrl;
  }
}
